import { emailValidate } from "@/validator/email";
import css from "@/components/diagnostics/QuestionsForm.module.scss";
import { FormSubmitButton } from "@/components/base/ui/FormSubmitButton";
import React, { useEffect, useState } from "react";
import * as Scroll from "react-scroll";
import { useForm } from "react-hook-form";
import { ItemizedQuestion as BusyQuestionType } from "@/data/busy";
import { CheckIcon } from "@heroicons/react/24/solid";

export const QuestionsForm = ({
  ItemizedQuestions,
  onSubmit,
  mainColor,
}: {
  ItemizedQuestions: BusyQuestionType[];
  onSubmit: (data: any) => Promise<void>;
  mainColor: string;
}) => {
  const [loading, setLoading] = useState(false);
  const [companyName, setCompanyName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [scores, setScores] = useState<number[]>([]);
  const scroll = Scroll.animateScroll;
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  useEffect(() => {
    if (scores.length > 0 && scores.length === ItemizedQuestions.length) {
      (async () => {
        await onSubmit({ companyName, email, scores });
        setScores([]);
      })();
    }
  }, [scores]);

  const onFinish = async (data: any) => {
    try {
      if (data.company_name) {
        setCompanyName(data.company_name);
      }

      if (data.email) {
        setEmail(data.email);
      }
      const currentResult = Object.entries(data).reduce((prev, current) => {
        const [key, value] = current;
        if (key === "company_name" || key === "email") {
          return prev;
        }
        const questionIndex = parseInt(key, 10);
        const currentQuestions =
          ItemizedQuestions[currentStep]?.questions || [];
        const answerLength =
          currentQuestions[questionIndex]?.answers.length || 0;
        const answerIndex = answerLength - parseInt(value as string, 10);
        const answer = currentQuestions[questionIndex]?.answers[answerIndex];

        if (answer) {
          return prev + (answer.score || 0);
        }
        return prev;
      }, 0);

      if (currentStep < ItemizedQuestions.length - 1) {
        setScores((prevScores) => [...prevScores, currentResult]);
        setCurrentStep(currentStep + 1);
        scroll.scrollToTop();
        reset();
      } else {
        setScores((prevScores) => [...prevScores, currentResult]);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit((data) => onFinish(data))}>
      {currentStep === 0 && (
        <>
          <div className={"pb-8 border-b border-gray-300"}>
            <label className={"block text-xl font-bold mb-2"}>会社名</label>
            <input
              type="text"
              placeholder="株式会社戦略デザインラボ"
              className={"bg-gray-100 p-4 w-full md:w-1/2"}
              {...register("company_name", {
                required: true,
              })}
            />
            {errors.company_name && (
              <span className={"block text-red-500 mt-2 text-sm"}>
                {"会社名を入力してください。"}
              </span>
            )}
          </div>
          <div className={"py-8 border-b border-gray-300"}>
            <div className={"mb-2"}>
              <label className={"block text-xl font-bold mb-2"}>
                メールアドレス
              </label>
              {/*<p className={"text-xs text-gray-600"}>*/}
              {/*  診断結果をメールにてお送りいたします*/}
              {/*</p>*/}
            </div>
            <input
              type="text"
              placeholder="sample@gmail.com"
              className={"bg-gray-100 p-4 w-full md:w-1/2"}
              {...register("email", {
                required: true,
                validate: async (value) => await emailValidate(value),
              })}
            />
            {errors.email && (
              <span className={"block text-red-500 mt-2 text-sm"}>
                {"メールアドレスを入力してください。"}
              </span>
            )}
          </div>
        </>
      )}
      <h2
        style={{ backgroundColor: mainColor }}
        className={"px-4 py-2 text-white font-bold mt-10 text-xl inline-block"}
      >
        Q.「{ItemizedQuestions[currentStep]?.type}」に関する質問
      </h2>
      {ItemizedQuestions[currentStep]?.questions.map(
        (question, questionIndex) => (
          <div key={questionIndex} className={"py-8 border-b border-gray-300"}>
            <div className={"flex mb-6 md:items-center"}>
              <p
                className={
                  "font-helvetica text-2xl italic mr-2 tracking-[0.8px] font-bold"
                }
                style={{ color: mainColor }}
              >
                Q
                {questionIndex +
                  1 +
                  currentStep *
                    (ItemizedQuestions[currentStep]?.questions.length || 1)}
              </p>
              <p className={"text-xl font-bold"}>{question.title}</p>
            </div>
            <div className={""}>
              {question.answers.map((answer, index) => (
                <div
                  className={"relative flex items-center mb-2 last:mb-0"}
                  key={index}
                >
                  <input
                    className={css.input}
                    type="radio"
                    id={`q-${questionIndex}-option-${index}`}
                    value={question.answers.length - index}
                    {...register(String(questionIndex), { required: true })}
                  />
                  <label
                    className={css.label}
                    htmlFor={`q-${questionIndex}-option-${index}`}
                  >
                    <div className={css.checkbox}>
                      <CheckIcon
                        className={
                          "fill-white absolute top-1/2 left-1/2 translate-x-[-50%] translate-y-[-50%] w-5"
                        }
                      />
                    </div>
                    <p className={"text-lg cursor-pointer"}>{answer.title}</p>
                  </label>
                </div>
              ))}
            </div>
            {Object.keys(errors).includes(String(questionIndex)) && (
              <span className={"block text-red-500 mt-4 text-sm"}>
                {"回答は必須です。"}
              </span>
            )}
          </div>
        )
      )}
      <div className={"mt-14"}>
        <FormSubmitButton loading={loading}>
          {currentStep === ItemizedQuestions.length - 1 ? "結果へ" : "次へ"}
        </FormSubmitButton>
      </div>
    </form>
  );
};
